<script setup lang="ts">
// Import useRouter for programmatic navigation
import { useRouter } from "vue-router";
import { useAuthStore } from "@/stores/auth";
import PublicHeader from "@/components/public/PublicHeader.vue";
import PublicFooter from "@/components/public/PublicFooter.vue";

// Get router instance and auth store
const router = useRouter();
const authStore = useAuthStore();

// Navigation handlers for register buttons
const navigateToRegister = (area: string) => {
  router.push(`/${area}/register`);
};

// Navigation handlers for login buttons
const navigateToDashboard = (area: string) => {
  router.push(`/${area}/dashboard`);
};

// Navigation handler for legacy registration
const navigateToLegacyRegistration = () => {
  router.push('/developer/legacy-registration');
};

// Header and Footer event handlers
const handleHelpClick = () => {
  console.log("Help button clicked");
  // TODO: Open help modal or navigate to help page
};

const handleLanguageChange = (language: string) => {
  console.log("Language changed to:", language);
  // TODO: Implement language switching logic
};

const handleFooterLinkClick = (section: string, item: string) => {
  console.log("Footer link clicked:", section, item);
  // TODO: Handle footer navigation
  // Could navigate to different pages or open modals based on the link
};
</script>

<template>
  <!-- Shared Public Header -->
  <PublicHeader 
    @help-click="handleHelpClick"
    @language-change="handleLanguageChange"
  />

<main id="main-content">
    <section id="hero-section" class="bg-gradient-to-br from-primary to-accent text-white h-[400px] flex items-center">
        <div class="max-w-7xl mx-auto px-5 w-full">
            <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
                <div>
                    <h1 class="text-4xl font-bold mb-4">{{ $t('landing.hero.title') }}</h1>
                    <p class="text-xl mb-6 text-gray-200">{{ $t('landing.hero.subtitle') }}</p>
                    <div class="flex flex-wrap gap-3">
                        <span class="bg-white/20 px-3 py-1 rounded-full text-sm">{{ $t('landing.hero.badges.article6') }}</span>
                        <span class="bg-white/20 px-3 py-1 rounded-full text-sm">{{ $t('landing.hero.badges.unfccc') }}</span>
                        <span class="bg-white/20 px-3 py-1 rounded-full text-sm">{{ $t('landing.hero.badges.blockchain') }}</span>
                    </div>
                </div>
                <div class="hidden lg:block">
                    <img class="w-full h-64 object-cover rounded-lg" src="https://storage.googleapis.com/uxpilot-auth.appspot.com/9471b09cab-9cfd63ae77513ad23c96.png" alt="modern carbon credit registry dashboard interface with green environmental theme" />
                </div>
            </div>
        </div>
    </section>

    <section id="access-tiles" class="py-16">
        <div class="max-w-7xl mx-auto px-5">
            <div class="text-center mb-12">
                <h2 class="text-3xl font-bold text-accent mb-4">{{ $t('landing.access.title') }}</h2>
                <p class="text-lg text-gray-600">{{ $t('landing.access.subtitle') }}</p>
            </div>
            
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div id="developer-tile" class="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow cursor-pointer group">
                    <div class="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-primary group-hover:text-white transition-colors">
                        <i class="fas fa-building text-primary group-hover:text-white text-xl"></i>
                    </div>
                    <h3 class="text-lg font-semibold text-accent mb-2">{{ $t('landing.access.developer.title') }}</h3>
                    <p class="text-gray-600 text-sm mb-4">{{ $t('landing.access.developer.description') }}</p>
                    <div class="space-y-2">
                        <button @click="navigateToRegister('developer')" class="w-full bg-primary text-white py-2 px-4 rounded-md text-sm font-medium hover:bg-primary/90">{{ $t('landing.access.developer.register') }}</button>
                        <button @click="navigateToDashboard('developer')" class="w-full border border-primary text-primary py-2 px-4 rounded-md text-sm font-medium hover:bg-primary/5">{{ $t('landing.access.developer.login') }}</button>
                    </div>
                </div>

                <div id="auditor-tile" class="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow cursor-pointer group">
                    <div class="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-secondary group-hover:text-accent transition-colors">
                        <i class="fas fa-clipboard-check text-secondary group-hover:text-accent text-xl"></i>
                    </div>
                    <h3 class="text-lg font-semibold text-accent mb-2">{{ $t('landing.access.auditor.title') }}</h3>
                    <p class="text-gray-600 text-sm mb-4">{{ $t('landing.access.auditor.description') }}</p>
                    <div class="space-y-2">
                        <button @click="navigateToRegister('auditor')" class="w-full bg-secondary text-accent py-2 px-4 rounded-md text-sm font-medium hover:bg-secondary/90">{{ $t('landing.access.auditor.register') }}</button>
                        <button @click="navigateToDashboard('auditor')" class="w-full border border-secondary text-secondary py-2 px-4 rounded-md text-sm font-medium hover:bg-secondary/5">{{ $t('landing.access.auditor.login') }}</button>
                    </div>
                </div>

                <div id="trader-tile" class="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow cursor-pointer group">
                    <div class="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-accent group-hover:text-white transition-colors">
                        <i class="fas fa-exchange-alt text-accent group-hover:text-white text-xl"></i>
                    </div>
                    <h3 class="text-lg font-semibold text-accent mb-2">{{ $t('landing.access.trader.title') }}</h3>
                    <p class="text-gray-600 text-sm mb-4">{{ $t('landing.access.trader.description') }}</p>
                    <div class="space-y-2">
                        <button @click="navigateToRegister('trader')" class="w-full bg-accent text-white py-2 px-4 rounded-md text-sm font-medium hover:bg-accent/90">{{ $t('landing.access.trader.register') }}</button>
                        <button @click="navigateToDashboard('trader')" class="w-full border border-accent text-accent py-2 px-4 rounded-md text-sm font-medium hover:bg-accent/5">{{ $t('landing.access.trader.login') }}</button>
                    </div>
                </div>

                <div id="government-tile" class="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow cursor-pointer group">
                    <div class="w-12 h-12 bg-yellow-500/10 rounded-lg flex items-center justify-center mb-4 group-hover:bg-yellow-500 group-hover:text-white transition-colors">
                        <i class="fas fa-university text-yellow-600 group-hover:text-white text-xl"></i>
                    </div>
                    <h3 class="text-lg font-semibold text-accent mb-2">{{ $t('landing.access.government.title') }}</h3>
                    <p class="text-gray-600 text-sm mb-4">{{ $t('landing.access.government.description') }}</p>
                    <div class="space-y-2">
                        <button @click="navigateToRegister('government')" class="w-full bg-primary text-white py-2 px-4 rounded-md text-sm font-medium hover:bg-primary/90">{{ $t('landing.access.government.register') }}</button>
                        <button @click="navigateToDashboard('government')" class="w-full bg-yellow-600 text-white py-2 px-4 rounded-md text-sm font-medium hover:bg-yellow-700">{{ $t('landing.access.government.login') }}</button>
                    </div>
                </div>
            </div>

            <div id="legacy-section" class="mt-8 bg-amber-50 border border-amber-200 rounded-lg p-6">
                <div class="flex items-start space-x-3">
                    <i class="fas fa-archive text-amber-600 text-xl mt-1"></i>
                    <div class="flex-1">
                        <h3 class="text-lg font-semibold text-amber-800 mb-2">{{ $t('landing.access.legacy.title') }}</h3>
                        <p class="text-amber-700 mb-4">{{ $t('landing.access.legacy.description') }}</p>
                        <button @click="navigateToLegacyRegistration" class="bg-amber-600 text-white py-2 px-4 rounded-md text-sm font-medium hover:bg-amber-700">
                            {{ $t('landing.access.legacy.button') }}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </section>

    <section id="announcements" class="py-12 bg-white">
        <div class="max-w-7xl mx-auto px-5">
            <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
                <div id="notices-panel" class="lg:col-span-2">
                    <h2 class="text-2xl font-bold text-accent mb-6">{{ $t('landing.announcements.title') }}</h2>
                    <div class="space-y-4">
                        <div class="border border-gray-200 rounded-lg p-4">
                            <div class="flex items-start justify-between">
                                <div class="flex-1">
                                    <h3 class="font-semibold text-accent mb-1">{{ $t('landing.announcements.items.doe_guidelines.title') }}</h3>
                                    <p class="text-gray-600 text-sm mb-2">{{ $t('landing.announcements.items.doe_guidelines.description') }}</p>
                                    <span class="text-xs text-gray-500">{{ $t('landing.announcements.items.doe_guidelines.date') }}</span>
                                </div>
                                <i class="fas fa-chevron-right text-gray-400"></i>
                            </div>
                        </div>
                        
                        <div class="border border-gray-200 rounded-lg p-4">
                            <div class="flex items-start justify-between">
                                <div class="flex-1">
                                    <h3 class="font-semibold text-accent mb-1">{{ $t('landing.announcements.items.article6_update.title') }}</h3>
                                    <p class="text-gray-600 text-sm mb-2">{{ $t('landing.announcements.items.article6_update.description') }}</p>
                                    <span class="text-xs text-gray-500">{{ $t('landing.announcements.items.article6_update.date') }}</span>
                                </div>
                                <i class="fas fa-chevron-right text-gray-400"></i>
                            </div>
                        </div>

                        <div class="border border-gray-200 rounded-lg p-4">
                            <div class="flex items-start justify-between">
                                <div class="flex-1">
                                    <h3 class="font-semibold text-accent mb-1">{{ $t('landing.announcements.items.maintenance.title') }}</h3>
                                    <p class="text-gray-600 text-sm mb-2">{{ $t('landing.announcements.items.maintenance.description') }}</p>
                                    <span class="text-xs text-gray-500">{{ $t('landing.announcements.items.maintenance.date') }}</span>
                                </div>
                                <i class="fas fa-chevron-right text-gray-400"></i>
                            </div>
                        </div>
                    </div>
                </div>

                <div id="downloads-panel">
                    <h2 class="text-2xl font-bold text-accent mb-6">{{ $t('landing.downloads.title') }}</h2>
                    <div class="space-y-3">
                        <span class="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                            <div class="flex items-center space-x-3">
                                <i class="fas fa-file-pdf text-red-500"></i>
                                <span class="text-sm font-medium">{{ $t('landing.downloads.items.pin_template') }}</span>
                            </div>
                            <i class="fas fa-download text-gray-400"></i>
                        </span>
                        
                        <span class="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                            <div class="flex items-center space-x-3">
                                <i class="fas fa-file-pdf text-red-500"></i>
                                <span class="text-sm font-medium">{{ $t('landing.downloads.items.pdd_template') }}</span>
                            </div>
                            <i class="fas fa-download text-gray-400"></i>
                        </span>
                        
                        <span class="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                            <div class="flex items-center space-x-3">
                                <i class="fas fa-file-pdf text-red-500"></i>
                                <span class="text-sm font-medium">{{ $t('landing.downloads.items.npramp_guide') }}</span>
                            </div>
                            <i class="fas fa-download text-gray-400"></i>
                        </span>
                        
                        <span class="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer">
                            <div class="flex items-center space-x-3">
                                <i class="fas fa-file-pdf text-red-500"></i>
                                <span class="text-sm font-medium">{{ $t('landing.downloads.items.user_manual') }}</span>
                            </div>
                            <i class="fas fa-download text-gray-400"></i>
                        </span>
                    </div>
                </div>
            </div>
        </div>
    </section>

    <section id="registry-explorer" class="py-12 bg-gray-50">
        <div class="max-w-7xl mx-auto px-5">
            <div class="text-center mb-8">
                <h2 class="text-3xl font-bold text-accent mb-4">{{ $t('landing.explorer.title') }}</h2>
                <p class="text-lg text-gray-600">{{ $t('landing.explorer.subtitle') }}</p>
            </div>
            
            <div class="bg-white rounded-lg border border-gray-200 p-6">
                <div class="flex flex-wrap items-center justify-between gap-4 mb-6">
                    <div class="flex flex-wrap gap-4">
                        <select class="border border-gray-300 rounded-md px-3 py-2 text-sm">
                            <option>{{ $t('landing.explorer.filters.all_categories') }}</option>
                            <option>Category A</option>
                            <option>Category B</option>
                            <option>Category C</option>
                        </select>
                        <select class="border border-gray-300 rounded-md px-3 py-2 text-sm">
                            <option>{{ $t('landing.explorer.filters.all_status') }}</option>
                            <option>Active</option>
                            <option>Under Review</option>
                            <option>Registered</option>
                        </select>
                    </div>
                    <button class="bg-primary text-white px-4 py-2 rounded-md text-sm font-medium">
                        <i class="fas fa-search mr-2"></i>{{ $t('landing.explorer.filters.search_button') }}
                    </button>
                </div>
                
                <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div class="text-center p-4 border border-gray-200 rounded-lg">
                        <div class="text-2xl font-bold text-primary mb-1">1,247</div>
                        <div class="text-sm text-gray-600">{{ $t('landing.explorer.stats.active_projects') }}</div>
                    </div>
                    <div class="text-center p-4 border border-gray-200 rounded-lg">
                        <div class="text-2xl font-bold text-secondary mb-1">2.4M</div>
                        <div class="text-sm text-gray-600">{{ $t('landing.explorer.stats.credits_issued') }}</div>
                    </div>
                    <div class="text-center p-4 border border-gray-200 rounded-lg">
                        <div class="text-2xl font-bold text-accent mb-1">847K</div>
                        <div class="text-sm text-gray-600">{{ $t('landing.explorer.stats.credits_retired') }}</div>
                    </div>
                </div>
            </div>
        </div>
    </section>
</main>

  <!-- Shared Public Footer -->
  <PublicFooter 
    @link-click="handleFooterLinkClick"
  />
</template>